import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CircleCheck,
  CirclePause,
  FileDown,
  FileText,
  Layers,
  Pencil,
  Plus,
  Settings2,
  Tags,
  Trash2,
  Users,
} from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FeatureSwitch from '../../components/FeatureSwitch'
import useFeatureToggle from '../../components/useFeatureToggle'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import { DeniedState, ErrorState } from '../../components/States'
import RowActions from '../../components/RowActions'
import SectionNav from '../../components/SectionNav'
import StatStrip from '../../components/StatStrip'
import useHashTab from '../../components/useHashTab'
import { useToast } from '../../components/toastContext'
import { dateColumn, flagColumn, indexColumn, statusColumn } from './cells'
import {
  deletePolicy,
  getPoliciesPage,
  listPolicies,
  setPoliciesEnabled,
  setPolicyState,
} from './engagementService'
import PolicyCategoriesPanel from './PolicyCategoriesPanel'
import PolicySettingsPanel from './PolicySettingsPanel'
import PolicyUsersDrawer from './PolicyUsersDrawer'
import { engagementStats as ST, policiesStrings as S } from './strings'
import './engagement.css'
import { serverText } from '../../lib/i18n'

const SECTIONS = ['policies', 'categories', 'settings']

/**
 * إدارة سياسات الأمن السيبراني — the legacy `WorkspacePoliciesSettings`.
 *
 * THREE SECTIONS, because the legacy screen is three tabs and stacking them would make one
 * long scroll: the policies themselves, the categories they are filed under, and the four
 * settings that decide how a user meets them.
 *
 * WHAT IS NOT WIRED: `إرسال بريد`. The legacy `SendEmail` has no counterpart in the API and
 * the button is registered in COSMETIC_UI.md. Everything else on the screen is live.
 */
export default function PoliciesPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [tab, setTab] = useHashTab('policies', SECTIONS)
  /* REMEMBERED, not plain state: the page came back on a Back and the search that chose
     those rows did not, so page 3 returned over a list nobody had searched. */
  const [query, setQuery] = useRemembered('policies:query', '')
  const [rows, setRows] = useState([])
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one: without this the table renders its "no records"
     state for a database fault, which tells the operator the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingState, setPendingState] = useState(null)
  /** The policy whose users are open, or `null`. */
  const [usersOf, setUsersOf] = useState(null)
  /* Same for the scope card — it decides WHICH policies those page numbers count. */
  const [scope, setScope] = useRemembered('policies:scope', null)

  /* The form is its own page (#1412): two names, two documents, an enforcement date
     and a picker over a directory of thousands is not a sheet's worth of screen. The
     row travels with the click so editing opens filled in without a second request. */
  const openForm = useCallback(
    (policy) =>
      navigate(policy ? `/policies/${policy.id}/edit` : '/policies/new', {
        state: policy ? { policy } : undefined,
      }),
    [navigate],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([listPolicies(), getPoliciesPage()])
      .then(([list, page]) => {
        if (cancelled) return
        setLoadError(null)
        setRows(list.rows)
        setSettings(page)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey, toast])

  /* Shared with every other activation switch — see useFeatureToggle. The flag
     lives inside `settings` here, which is why the hook takes a setter. */
  const { toggle: toggleFeature } = useFeatureToggle({
    setValue: (enabled) => setSettings((previous) => ({ ...previous, enabled })),
    save: setPoliciesEnabled,
  })

  const applyState = async () => {
    const policy = pendingState
    setPendingState(null)
    const outcome = await setPolicyState(policy.id, !policy.isActive)
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.saved)
    setReloadKey((key) => key + 1)
  }

  const remove = async () => {
    const policy = pendingDelete
    setPendingDelete(null)
    const outcome = await deletePolicy(policy.id)
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.deleted)
    setReloadKey((key) => key + 1)
  }

  /* Counted off the rows already on the page; the two state cards narrow the
     list to what they count. */
  const stats = useMemo(() => {
    const active = rows.filter((row) => row.isActive).length
    return { total: rows.length, active, inactive: rows.length - active }
  }, [rows])

  const visibleRows = useMemo(() => {
    if (scope === 'active') return rows.filter((row) => row.isActive)
    if (scope === 'inactive') return rows.filter((row) => !row.isActive)
    return rows
  }, [rows, scope])

  const columns = useMemo(
    () => [
      indexColumn(S.columns.index),
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'nameEn',
        header: S.columns.nameEn,
        render: (row) => <bdi dir="ltr">{row.nameEn}</bdi>,
      },
      { key: 'version', header: S.columns.version, align: 'center' },
      dateColumn('createdOn', S.columns.createdOn),
      flagColumn('forceUsers', S.columns.forceUsers, S),
      dateColumn('evaluationStartDate', S.columns.evaluationStartDate),
      statusColumn('isActive', S.columns.status, S),
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '130px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              /* The legacy `/UsersPoliciesDetails` screen, as a drawer over this list. */
              {
                id: 'users',
                icon: Users,
                label: S.users.open,
                tone: 'violet',
                onClick: () => setUsersOf(row),
              },
              {
                id: 'state',
                /* The pair the status column beside it already speaks: a paused circle for
                   the policy that is on and about to stop, a ticked one for the policy that
                   is off. `Power` was one glyph for both directions — a switch that looks
                   the same whichever way it is about to go says nothing about what pressing
                   it does, and on a row whose state is already written two columns away it
                   only asked to be read twice. */
                icon: row.isActive ? CirclePause : CircleCheck,
                label: row.isActive ? S.state.deactivate : S.state.activate,
                /* The colours the status tiles above the table already use for these two
                   states — amber paused, green live. */
                tone: row.isActive ? 'warning' : 'success',
                onClick: () => setPendingState(row),
              },
              /* THE TWO DOCUMENTS, as the legacy row menu offers them: each opens in a
                 new tab, where the browser previews the PDF and offers to save it. An
                 entry is only there when that language HAS a file — a policy can be
                 saved with one of the two. */
              row.fileAr && {
                id: 'fileAr',
                icon: FileDown,
                label: S.row.fileAr,
                tone: 'primary',
                as: 'a',
                href: row.fileAr,
                target: '_blank',
                rel: 'noreferrer',
              },
              row.fileEn && {
                id: 'fileEn',
                icon: FileDown,
                label: S.row.fileEn,
                tone: 'primary',
                as: 'a',
                href: row.fileEn,
                target: '_blank',
                rel: 'noreferrer',
              },
              /* `إرسال بريد` IS NOT HERE. Nothing in this slice sends one — the
                 legacy's send action has no counterpart in the port, and a mail button
                 that opens nothing is worse than its absence. COSMETIC_UI.md. */
              { id: 'edit', icon: Pencil, label: S.row.edit, onClick: () => openForm(row) },
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [openForm],
  )

  const sectionGroups = useMemo(
    () => [
      {
        items: [
          { id: 'policies', label: S.sections.policies, icon: FileText },
          { id: 'categories', label: S.sections.categories, icon: Tags },
          { id: 'settings', label: S.sections.settings, icon: Settings2 },
        ],
      },
    ],
    [],
  )

  if (loadError) {
    return (
      <>
        <PageHeader title={S.title} subtitle={S.subtitle} />
        {loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState
            title={S.actionFailed}
            description={loadError.message}
          />
        )}
      </>
    )
  }

  if (loading || !settings) {
    return (
      <>
        <PageHeader title={S.title} subtitle={S.subtitle} />
        <PageLoader />
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        count={tab === 'policies' ? rows.length : undefined}
        actions={
          <>
            <FeatureSwitch
              checked={settings.enabled}
              onChange={toggleFeature}
              label={S.enableFeature}
            />
            {tab === 'policies' && (
              <Button onClick={() => openForm(null)}>
                <Plus size={16} strokeWidth={2} />
                {S.create}
              </Button>
            )}
          </>
        }
      />

      <div className="eng__workspace">
        <SectionNav
          groups={sectionGroups}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        <div className="eng__panel">
          {tab === 'policies' && (
            <>
              <StatStrip
                label={ST.caption}
                activeId={scope ?? 'all'}
                onSelect={(id) => {
                  /* ALL IS NOT A FILTER TO TOGGLE, it is the absence of one, so it clears
                     the scope outright rather than flipping it back on when clicked
                     twice. */
                  if (id === 'all') return setScope(null)
                  setScope((current) => (current === id ? null : id))
                }}
                items={[
                  {
                    /* Was an unclickable figure labelled "Total": the only way back to the
                       full list was to click the selected card a second time. */
                    id: 'all',
                    icon: Layers,
                    label: S.allPolicies,
                    value: stats.total,
                    tone: 'violet',
                  },
                  {
                    id: 'active',
                    icon: CircleCheck,
                    label: ST.active,
                    value: stats.active,
                    tone: 'success',
                  },
                  {
                    id: 'inactive',
                    icon: CirclePause,
                    label: ST.inactive,
                    value: stats.inactive,
                    tone: 'warning',
                  },
                ]}
              />

              <FilterBar
                search={{ value: query, onChange: setQuery, placeholder: S.columns.name }}
              />

              <DataTable
                error={loadError}
                columns={columns}
                rows={visibleRows}
                query={query}
                /* The scope card sits outside the table, which cannot see it — narrowing
                   to the inactive policies kept a page the shorter list does not have. */
                resetKey={scope}
                controls={false}
                getRowId={(row) => row.id}
                initialSort={{ key: 'name', dir: 'asc' }}
              />
            </>
          )}

          {tab === 'categories' && <PolicyCategoriesPanel />}

          {tab === 'settings' && (
            <PolicySettingsPanel
              settings={settings}
              onChange={(patch) => setSettings((previous) => ({ ...previous, ...patch }))}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={!!pendingState}
        title={pendingState?.isActive ? S.deactivateTitle : S.activateTitle}
        tone={pendingState?.isActive ? 'warning' : 'success'}
        message={pendingState?.isActive ? S.deactivateConfirm : S.activateConfirm}
        confirmLabel={pendingState?.isActive ? S.state.deactivate : S.state.activate}
        cancelLabel={S.close}
        onConfirm={applyState}
        onCancel={() => setPendingState(null)}
      />

      <PolicyUsersDrawer open={!!usersOf} policy={usersOf} onClose={() => setUsersOf(null)} />
    </>
  )
}
